/**
 * ความคืบหน้าการชำระเงิน (แยกจ่าย) — sync จาก PaymentDialog ไป Customer Display
 */

const STORAGE_KEY = "posCustomerDisplayPaymentProgress";
const EVENT_NAME = "customer-display-payment-progress-updated";

export type CustomerDisplayPaymentRow = {
  id: string;
  methodName: string;
  amount: number;
  status: "entered";
};

export type CustomerDisplayPaymentProgress = {
  grandTotal: number;
  paidTotal: number;
  remainingDue: number;
  change: number;
  isSplit: boolean;
  isComplete: boolean;
  rows: CustomerDisplayPaymentRow[];
  qrPayAmount?: number;
};

export function isQrLikePaymentMethodType(
  type: string | undefined | null,
): boolean {
  return type === "QR" || type === "BANK_TRANSFER" || type === "WALLET";
}

type PaymentProgressMethod = {
  id: string;
  displayName: string;
  type: string;
};

type PaymentProgressRowInput = {
  id: string;
  methodId: string;
  amount: number;
};

function computeQrPayAmount(input: {
  paymentRows: PaymentProgressRowInput[];
  methodMap: Map<string, PaymentProgressMethod>;
  isSplit: boolean;
  remainingDue: number;
  grandTotal: number;
}): number | undefined {
  const { paymentRows, methodMap, isSplit, remainingDue, grandTotal } = input;

  const qrRowsWithAmount = paymentRows
    .map((row) => ({
      row,
      method: methodMap.get(row.methodId),
      amount: row.amount,
    }))
    .filter(
      ({ method, amount }) =>
        method && isQrLikePaymentMethodType(method.type) && amount > 0,
    );

  if (qrRowsWithAmount.length === 1) {
    return qrRowsWithAmount[0]?.amount;
  }

  if (qrRowsWithAmount.length > 1) {
    return qrRowsWithAmount.reduce((sum, entry) => sum + entry.amount, 0);
  }

  const hasQrMethodSelected = paymentRows.some((row) => {
    const method = methodMap.get(row.methodId);
    return method && isQrLikePaymentMethodType(method.type);
  });

  if (hasQrMethodSelected && remainingDue > 0) {
    return remainingDue;
  }

  if (!isSplit && paymentRows.length === 1) {
    const row = paymentRows[0];
    if (!row) return undefined;
    const method = methodMap.get(row.methodId);
    if (method && isQrLikePaymentMethodType(method.type)) {
      return row.amount > 0 ? row.amount : grandTotal > 0 ? grandTotal : undefined;
    }
  }

  return undefined;
}

export function buildCustomerDisplayPaymentProgress(input: {
  paymentRows: PaymentProgressRowInput[];
  paymentMethods: PaymentProgressMethod[];
  grandTotal: number;
  paidTotal: number;
  remainingDue: number;
  change: number;
}): CustomerDisplayPaymentProgress {
  const { paymentRows, paymentMethods, grandTotal, paidTotal, remainingDue, change } =
    input;
  const methodMap = new Map(paymentMethods.map((m) => [m.id, m]));
  const isSplit =
    paymentRows.length > 1 ||
    (paidTotal > 0 && remainingDue > 0 && grandTotal > 0);

  const rows: CustomerDisplayPaymentRow[] = paymentRows
    .filter((row) => row.methodId && row.amount > 0)
    .map((row) => {
      const method = methodMap.get(row.methodId);
      return {
        id: row.id,
        methodName: method?.displayName ?? row.methodId,
        amount: row.amount,
        status: "entered" as const,
      };
    });

  const qrPayAmount = computeQrPayAmount({
    paymentRows,
    methodMap,
    isSplit,
    remainingDue,
    grandTotal,
  });

  return {
    grandTotal,
    paidTotal,
    remainingDue,
    change,
    isSplit,
    isComplete: paidTotal >= grandTotal,
    rows,
    qrPayAmount,
  };
}

export function getCustomerDisplayPaymentProgress(): CustomerDisplayPaymentProgress | null {
  if (typeof window === "undefined") return null;
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CustomerDisplayPaymentProgress;
    if (
      typeof parsed?.grandTotal === "number" &&
      typeof parsed?.paidTotal === "number" &&
      typeof parsed?.remainingDue === "number" &&
      typeof parsed?.isSplit === "boolean"
    ) {
      return parsed;
    }
  } catch {
    localStorage.removeItem(STORAGE_KEY);
  }
  return null;
}

export function setCustomerDisplayPaymentProgress(
  data: CustomerDisplayPaymentProgress,
): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  window.dispatchEvent(new CustomEvent(EVENT_NAME));
}

export function clearCustomerDisplayPaymentProgress(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(EVENT_NAME));
}

export function addCustomerDisplayPaymentProgressListener(
  callback: () => void,
): () => void {
  const handler = () => callback();
  const storageHandler = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) callback();
  };
  window.addEventListener(EVENT_NAME, handler);
  window.addEventListener("storage", storageHandler);
  return () => {
    window.removeEventListener(EVENT_NAME, handler);
    window.removeEventListener("storage", storageHandler);
  };
}
